import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ACCOUNT_TIER_OPTIONS } from '@/lib/engagementPersonaUtils';
import {
  emptyEngagementViewConfig,
  NUMERIC_OP_OPTIONS,
  normalizeViewConfigFromApi,
  serializeViewConfigForApi,
} from '@/lib/engagementSavedViews';

function MultiSelectGroup({ label, options, selected, onChange }) {
  if (!options?.length) {
    return (
      <div>
        <Label className="text-xs text-gray-600">{label}</Label>
        <p className="text-xs text-muted-foreground mt-1">No values in loaded data.</p>
      </div>
    );
  }
  return (
    <div>
      <Label className="text-xs text-gray-600 mb-2 block">{label}</Label>
      <div className="max-h-36 overflow-y-auto border rounded-md p-2 space-y-1">
        {options.map((opt) => {
          const checked = selected.includes(opt);
          return (
            <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer py-0.5">
              <Checkbox
                checked={checked}
                onCheckedChange={(next) => {
                  onChange(
                    next
                      ? [...selected, opt]
                      : selected.filter((x) => x !== opt),
                  );
                }}
              />
              <span className="truncate">{opt}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function NumericFilterRow({ label, value, onChange }) {
  return (
    <div>
      <Label className="text-xs text-gray-600 mb-1 block">{label}</Label>
      <div className="flex gap-2">
        <Select value={value.op} onValueChange={(op) => onChange({ ...value, op })}>
          <SelectTrigger className="w-20 h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {NUMERIC_OP_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="number"
          min={0}
          className="h-9 flex-1"
          placeholder="Value"
          value={value.value}
          onChange={(e) => onChange({ ...value, value: e.target.value })}
        />
      </div>
    </div>
  );
}

export default function EngagementViewFormDialog({
  open,
  onOpenChange,
  mode,
  initialName,
  initialViewConfig,
  archetypeOptions,
  departmentOptions,
  accountTypeOptions,
  productAffinityOptions,
  onSave,
  saving,
  nameError,
}) {
  const [name, setName] = useState('');
  const [form, setForm] = useState(emptyEngagementViewConfig());

  useEffect(() => {
    if (!open) return;
    setName(initialName || '');
    setForm(normalizeViewConfigFromApi(initialViewConfig || {}));
  }, [open, initialName, initialViewConfig]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave?.({
      name: name.trim(),
      view_config: serializeViewConfigForApi(form),
    });
  };

  const title = mode === 'edit' ? 'Edit saved view' : 'New saved view';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="view-name" className="text-xs text-gray-600">
                View name
              </Label>
              <Input
                id="view-name"
                className="mt-1"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Champions — untouched"
                required
              />
              {nameError ? (
                <p className="text-xs text-red-600 mt-1">{nameError}</p>
              ) : null}
            </div>

            <MultiSelectGroup
              label="Archetype"
              options={archetypeOptions}
              selected={form.archetypes}
              onChange={(archetypes) => setForm((f) => ({ ...f, archetypes }))}
            />

            <MultiSelectGroup
              label="Account tier"
              options={ACCOUNT_TIER_OPTIONS}
              selected={form.tiers}
              onChange={(tiers) => setForm((f) => ({ ...f, tiers }))}
            />

            <MultiSelectGroup
              label="Account type"
              options={accountTypeOptions}
              selected={form.account_types}
              onChange={(account_types) => setForm((f) => ({ ...f, account_types }))}
            />

            <MultiSelectGroup
              label="Department"
              options={departmentOptions}
              selected={form.departments}
              onChange={(departments) => setForm((f) => ({ ...f, departments }))}
            />

            <div>
              <Label className="text-xs text-gray-600">Persona fit (minimum)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                className="mt-1 h-9"
                placeholder="e.g. 70"
                value={form.persona_fit_score_min}
                onChange={(e) =>
                  setForm((f) => ({ ...f, persona_fit_score_min: e.target.value }))
                }
              />
            </div>

            <div>
              <Label className="text-xs text-gray-600">Role title contains</Label>
              <Input
                className="mt-1 h-9"
                placeholder="Substring match"
                value={form.role_title_q}
                onChange={(e) => setForm((f) => ({ ...f, role_title_q: e.target.value }))}
              />
            </div>

            <MultiSelectGroup
              label="Product affinity"
              options={productAffinityOptions}
              selected={form.product_affinity}
              onChange={(product_affinity) => setForm((f) => ({ ...f, product_affinity }))}
            />

            <NumericFilterRow
              label="Touches"
              value={form.touches}
              onChange={(touches) => setForm((f) => ({ ...f, touches }))}
            />

            <NumericFilterRow
              label="Last touch (days ago)"
              value={form.last_touch_days}
              onChange={(last_touch_days) => setForm((f) => ({ ...f, last_touch_days }))}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
